import React, { useState, useMemo } from 'react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import SafeIcon from '../components/common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { clsx } from 'clsx';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { useApp } from '../context/AppContext';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LineChart,
  Line,
  Legend,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  PieChart,
  Pie
} from 'recharts';

export const MonthlyPerformance = () => {
  const { data } = useApp();
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [selectedMinistry, setSelectedMinistry] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // overview, ministries, projects, compliance, budget

  // Monthly performance data
  const monthlyData = {
    month: format(selectedMonth, 'MMMM yyyy'),
    reportGenerated: new Date().toISOString(),
    executiveSummary: {
      overallScore: 68,
      trend: 'up', // up, down, stable
      trendValue: '+3%',
      keyHighlights: [
        'Budget execution improved to 68% (up from 65% previous month)',
        '18 major project milestones achieved on schedule',
        '3 critical compliance breaches requiring immediate attention',
        'NDP6 Pillar 3 (Environmental Sustainability) exceeding targets at 88%'
      ],
      criticalIssues: [
        {
          severity: 'critical',
          issue: 'Ministry of Works & Transport: 12 days overdue on PPU acknowledgment',
          impact: 'Legal non-compliance, potential contract suspension',
          action: 'Escalated to Permanent Secretary, response due 48 hours'
        },
        {
          severity: 'high',
          issue: 'Ministry of Health: Budget execution at 55% (target 65%)',
          impact: 'Underspending risk, capital projects delayed',
          action: 'Budget acceleration plan requested within 14 days'
        },
        {
          severity: 'high',
          issue: '6 ministries late on monthly expenditure reports',
          impact: 'Delayed treasury reconciliation',
          action: 'Reminder notices sent, deadline extended 5 days'
        }
      ]
    },
    ministryPerformance: [
      {
        id: 'MEFT',
        name: 'Ministry of Environment, Forestry & Tourism',
        score: 92,
        previousScore: 90,
        trend: 'up',
        rank: 1,
        kpis: {
          projectDelivery: 95,
          budgetExecution: 90,
          complianceRate: 100,
          reportingTimeliness: 100,
          ndp6Progress: 88
        },
        highlights: [
          'All NDP6 targets exceeded',
          'Zero compliance breaches',
          'Environmental clearances processed 20% faster'
        ],
        concerns: []
      },
      {
        id: 'MOE',
        name: 'Ministry of Education, Arts & Culture',
        score: 78,
        previousScore: 76,
        trend: 'up',
        rank: 2,
        kpis: {
          projectDelivery: 85,
          budgetExecution: 78,
          complianceRate: 88,
          reportingTimeliness: 75,
          ndp6Progress: 78
        },
        highlights: [
          '12 school renovation projects completed',
          'Improved budget execution (+3%)',
          'Vocational training enrollment up 15%'
        ],
        concerns: [
          'Quarterly NDP6 report submitted 3 days late'
        ]
      },
      {
        id: 'MICT',
        name: 'Ministry of ICT',
        score: 75,
        previousScore: 72,
        trend: 'up',
        rank: 3,
        kpis: {
          projectDelivery: 80,
          budgetExecution: 82,
          complianceRate: 75,
          reportingTimeliness: 70,
          ndp6Progress: 75
        },
        highlights: [
          '8 new e-Government services launched',
          'Cybersecurity compliance improved to 75%',
          'Budget execution on track'
        ],
        concerns: [
          'System uptime below target (97% vs 99%)',
          'Delayed responses to parliamentary questions'
        ]
      },
      {
        id: 'MOHSS',
        name: 'Ministry of Health & Social Services',
        score: 68,
        previousScore: 70,
        trend: 'down',
        rank: 4,
        kpis: {
          projectDelivery: 72,
          budgetExecution: 55,
          complianceRate: 68,
          reportingTimeliness: 80,
          ndp6Progress: 68
        },
        highlights: [
          'Khomas Hospital Phase 1 foundation completed',
          'Monthly reports submitted on time',
          'Rural clinic staffing improved'
        ],
        concerns: [
          'Budget execution critically low at 55%',
          '18 outstanding AG recommendations',
          'Staff vacancy rate at 32%'
        ]
      },
      {
        id: 'MWT',
        name: 'Ministry of Works & Transport',
        score: 58,
        previousScore: 60,
        trend: 'down',
        rank: 5,
        kpis: {
          projectDelivery: 65,
          budgetExecution: 58,
          complianceRate: 45,
          reportingTimeliness: 72,
          ndp6Progress: 58
        },
        highlights: [
          'Windhoek-Okahandja bridge deck 68% complete',
          'Rural road maintenance program on track'
        ],
        concerns: [
          'Critical procurement compliance failures (45%)',
          'PPU non-compliance acknowledgment 12 days overdue',
          'Budget execution below target',
          '8 non-compliance notices from PPU'
        ]
      }
    ],
    projectMilestones: {
      achieved: 18,
      missed: 4,
      onTrack: 42,
      atRisk: 15,
      delayed: 8,
      majorAchievements: [
        {
          project: 'Khomas District Hospital Phase 1',
          milestone: 'Foundation Works Complete',
          ministry: 'Ministry of Health',
          impact: 'On schedule for Q2 2025 structural framing'
        },
        {
          project: 'Windhoek-Okahandja Dual Carriageway',
          milestone: 'Bridge Deck 68% Complete',
          ministry: 'Ministry of Works',
          impact: 'Critical path milestone, no delays'
        },
        {
          project: 'Rural Electrification - Zambezi',
          milestone: 'Final Commissioning 90%',
          ministry: 'Ministry of Mines & Energy',
          impact: 'Expected completion ahead of schedule'
        }
      ],
      criticalDelays: [
        {
          project: 'School Renovation Program - Kunene',
          milestone: 'Roofing',
          ministry: 'Ministry of Education',
          daysDelayed: 45,
          reason: 'Contractor abandonment',
          action: 'Investigation initiated, contract termination pending'
        },
        {
          project: 'Green Scheme Irrigation Expansion',
          milestone: 'Pump Station Installation',
          ministry: 'Ministry of Agriculture',
          daysDelayed: 12,
          reason: 'Equipment delivery delays',
          action: 'Contractor performance review scheduled'
        }
      ]
    },
    complianceMetrics: {
      overallRate: 72,
      previousRate: 68,
      breaches: 8,
      warnings: 15,
      onTrack: 45,
      categories: [
        {
          category: 'Procurement Compliance',
          rate: 64,
          breaches: 5,
          target: 100,
          status: 'Critical'
        },
        {
          category: 'Financial Reporting',
          rate: 85,
          breaches: 2,
          target: 100,
          status: 'At Risk'
        },
        {
          category: 'NDP6 Reporting',
          rate: 75,
          breaches: 1,
          target: 100,
          status: 'At Risk'
        },
        {
          category: 'Audit Compliance',
          rate: 68,
          breaches: 0,
          target: 80,
          status: 'At Risk'
        },
        {
          category: 'Parliamentary Affairs',
          rate: 89,
          breaches: 0,
          target: 100,
          status: 'On Track'
        }
      ],
      topCompliantMinistries: [
        'Ministry of Environment',
        'Ministry of ICT',
        'Ministry of Education'
      ],
      bottomCompliantMinistries: [
        'Ministry of Works & Transport',
        'Ministry of Agriculture',
        'Ministry of Health'
      ]
    },
    budgetExecution: {
      nationalRate: 68,
      previousRate: 65,
      targetRate: 70,
      totalAllocated: 103500000000,
      totalDisbursed: 70380000000,
      variance: -2070000000,
      byMinistry: [
        { ministry: 'Environment', allocated: 8500000000, disbursed: 7480000000, rate: 88 },
        { ministry: 'ICT', allocated: 800000000, disbursed: 656000000, rate: 82 },
        { ministry: 'Education', allocated: 14000000000, disbursed: 10920000000, rate: 78 },
        { ministry: 'Agriculture', allocated: 2200000000, disbursed: 1320000000, rate: 60 },
        { ministry: 'Health', allocated: 9000000000, disbursed: 4950000000, rate: 55 },
        { ministry: 'Works', allocated: 6500000000, disbursed: 3770000000, rate: 58 }
      ],
      underspending: [
        {
          ministry: 'Ministry of Health',
          allocated: 9000000000,
          disbursed: 4950000000,
          gap: 4050000000,
          reason: 'Capital projects delayed, procurement bottlenecks',
          action: 'Budget acceleration plan due 14 days'
        },
        {
          ministry: 'Ministry of Agriculture',
          allocated: 2200000000,
          disbursed: 1320000000,
          gap: 880000000,
          reason: 'Seasonal spending patterns, irrigation delays',
          action: 'Monitoring ongoing, Q3 expected acceleration'
        }
      ]
    },
    ndp6Alignment: {
      pillars: [
        {
          name: 'Economic Advancement',
          progress: 58,
          target: 65,
          status: 'At Risk',
          variance: -7
        },
        {
          name: 'Social Transformation',
          progress: 72,
          target: 70,
          status: 'On Track',
          variance: +2
        },
        {
          name: 'Environmental Sustainability',
          progress: 88,
          target: 75,
          status: 'Exceeding',
          variance: +13
        },
        {
          name: 'Good Governance',
          progress: 64,
          target: 70,
          status: 'At Risk',
          variance: -6
        }
      ],
      topPerformingThemes: [
        'Climate Action (90%)',
        'Education & Skills (78%)',
        'Renewable Energy (92%)'
      ],
      underperformingThemes: [
        'Industrialization (52%)',
        'Anti-Corruption (58%)',
        'Infrastructure Development (62%)'
      ]
    },
    trendAnalysis: {
      last6Months: [
        { month: 'Aug 2024', score: 62, budget: 58, compliance: 65 },
        { month: 'Sep 2024', score: 64, budget: 60, compliance: 67 },
        { month: 'Oct 2024', score: 65, budget: 62, compliance: 68 },
        { month: 'Nov 2024', score: 66, budget: 64, compliance: 70 },
        { month: 'Dec 2024', score: 65, budget: 65, compliance: 68 },
        { month: 'Jan 2025', score: 68, budget: 68, compliance: 72 }
      ],
      insights: [
        'Steady improvement in overall performance (+6% over 6 months)',
        'Budget execution showing consistent upward trend',
        'Compliance rate volatile but trending positive',
        'December dip attributed to year-end reporting lag'
      ]
    },
    recommendations: [
      {
        priority: 'critical',
        recommendation: 'Immediate intervention required for Ministry of Works procurement compliance',
        rationale: '8 non-compliance notices and 12-day overdue acknowledgment pose legal risk',
        action: 'Emergency meeting with Permanent Secretary, potential legal action',
        timeline: '48 hours',
        responsible: 'Prime Minister\'s Office + Public Procurement Unit'
      },
      {
        priority: 'high',
        recommendation: 'Budget acceleration plans for Ministry of Health and Agriculture',
        rationale: 'Underspending risk threatens capital project delivery and budget reallocation',
        action: 'Mandatory acceleration plans with weekly monitoring',
        timeline: '14 days',
        responsible: 'Ministry of Finance + Respective OMAs'
      },
      {
        priority: 'medium',
        recommendation: 'Contractor performance review for abandoned projects',
        rationale: 'School Renovation Kunene abandoned for 45 days, contractor debarment potential',
        action: 'Forensic investigation, contract termination proceedings',
        timeline: '30 days',
        responsible: 'Prime Minister\'s Office Investigation Unit'
      },
      {
        priority: 'medium',
        recommendation: 'Capacity building for NDP6 reporting compliance',
        rationale: 'Recurring late submissions from 5 ministries indicate systemic capacity gaps',
        action: 'Training workshops, reporting templates, dedicated focal points',
        timeline: '60 days',
        responsible: 'National Planning Commission + OPM'
      }
    ]
  };

  const formatCurrency = (value) => {
    if (value >= 1e9) return `N$${(value / 1e9).toFixed(1)}B`;
    if (value >= 1e6) return `N$${(value / 1e6).toFixed(1)}M`;
    return `N$${value.toLocaleString()}`;
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-amber-500';
    return 'bg-red-500';
  };

  const tabs = [
    { id: 'overview', label: 'Executive Overview', icon: FiIcons.FiGrid },
    { id: 'ministries', label: 'Ministry Performance', icon: FiIcons.FiBuilding },
    { id: 'projects', label: 'Project Milestones', icon: FiIcons.FiBriefcase },
    { id: 'compliance', label: 'Compliance Status', icon: FiIcons.FiShield },
    { id: 'budget', label: 'Budget Execution', icon: FiIcons.FiDollarSign }
  ];

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Monthly Performance Review</h1>
          <p className="text-slate-500">
            Comprehensive monthly performance dashboard for Cabinet briefings and strategic oversight
          </p>
          <div className="flex items-center gap-3 mt-2 text-xs">
            <span className="px-2 py-1 bg-grn-navy text-white rounded font-bold">
              {monthlyData.month}
            </span>
            <span className="text-slate-500">
              Report Generated: {format(new Date(monthlyData.reportGenerated), 'dd MMM yyyy, HH:mm')}
            </span>
          </div>
        </div>
        <div className="flex gap-3">
          <select
            value={format(selectedMonth, 'yyyy-MM')}
            onChange={(e) => setSelectedMonth(new Date(e.target.value))}
            className="px-4 py-2 border border-slate-300 rounded-lg text-sm bg-white"
          >
            {Array.from({ length: 12 }, (_, i) => {
              const month = subMonths(new Date(), i);
              return (
                <option key={i} value={format(month, 'yyyy-MM')}>
                  {format(month, 'MMMM yyyy')}
                </option>
              );
            })}
          </select>
          <button className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium hover:bg-slate-50 flex items-center gap-2">
            <SafeIcon icon={FiIcons.FiDownload} />
            Export Report
          </button>
          <button className="px-4 py-2 bg-grn-navy text-white rounded-lg text-sm font-medium hover:bg-blue-900 flex items-center gap-2">
            <SafeIcon icon={FiIcons.FiShare2} />
            Share with Cabinet
          </button>
        </div>
      </div>

      {/* Executive Summary Card */}
      <Card className="bg-gradient-to-r from-grn-navy to-blue-900 text-white">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold mb-2">Executive Summary</h2>
            <p className="text-blue-100 text-sm">National Performance Overview - {monthlyData.month}</p>
          </div>
          <div className="text-right">
            <div className="text-5xl font-black mb-1">{monthlyData.executiveSummary.overallScore}%</div>
            <div className="flex items-center gap-2 justify-end">
              <SafeIcon
                icon={monthlyData.executiveSummary.trend === 'up' ? FiIcons.FiTrendingUp : FiIcons.FiTrendingDown}
                className={monthlyData.executiveSummary.trend === 'up' ? 'text-green-400' : 'text-red-400'}
              />
              <span className={monthlyData.executiveSummary.trend === 'up' ? 'text-green-400' : 'text-red-400'}>
                {monthlyData.executiveSummary.trendValue} vs last month
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Key Highlights */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider mb-3 opacity-80">
              Key Highlights
            </h3>
            <div className="space-y-2">
              {monthlyData.executiveSummary.keyHighlights.map((highlight, idx) => (
                <div key={idx} className="flex items-start gap-2 text-sm">
                  <SafeIcon icon={FiIcons.FiCheckCircle} className="text-green-400 mt-0.5 shrink-0" />
                  <span className="text-blue-100">{highlight}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Critical Issues */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider mb-3 opacity-80">
              Critical Issues Requiring Attention
            </h3>
            <div className="space-y-2">
              {monthlyData.executiveSummary.criticalIssues.slice(0, 3).map((issue, idx) => (
                <div
                  key={idx}
                  className={clsx(
                    'p-2 rounded border text-xs',
                    issue.severity === 'critical'
                      ? 'bg-red-900/30 border-red-500/50'
                      : 'bg-orange-900/30 border-orange-500/50'
                  )}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className={clsx(
                        'px-2 py-0.5 rounded text-xs font-bold uppercase',
                        issue.severity === 'critical' ? 'bg-red-500' : 'bg-orange-500'
                      )}
                    >
                      {issue.severity}
                    </span>
                    <span className="font-bold">{issue.issue}</span>
                  </div>
                  <p className="text-blue-200 text-xs mt-1">Action: {issue.action}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* Stats Dashboard */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="text-center p-4 border-l-4 border-l-grn-navy">
          <div className={clsx('text-3xl font-black', getScoreColor(monthlyData.executiveSummary.overallScore))}>
            {monthlyData.executiveSummary.overallScore}%
          </div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Overall Score</div>
        </Card>
        <Card className="text-center p-4 border-l-4 border-l-green-500">
          <div className="text-3xl font-black text-green-700">{monthlyData.projectMilestones.achieved}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Milestones Achieved</div>
        </Card>
        <Card className="text-center p-4 border-l-4 border-l-blue-500">
          <div className="text-3xl font-black text-blue-700">{monthlyData.budgetExecution.nationalRate}%</div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Budget Execution</div>
        </Card>
        <Card className="text-center p-4 border-l-4 border-l-amber-500">
          <div className="text-3xl font-black text-amber-700">{monthlyData.complianceMetrics.overallRate}%</div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Compliance Rate</div>
        </Card>
        <Card className="text-center p-4 border-l-4 border-l-red-600">
          <div className="text-3xl font-black text-red-700">{monthlyData.complianceMetrics.breaches}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Active Breaches</div>
        </Card>
      </div>

      {/* Tab Navigation */}
      <Card>
        <div className="flex gap-2 overflow-x-auto pb-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={clsx(
                'px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap',
                activeTab === tab.id
                  ? 'bg-grn-navy text-white'
                  : 'bg-white border border-slate-200 text-slate-600 hover:border-grn-navy/30'
              )}
            >
              <SafeIcon icon={tab.icon} />
              {tab.label}
            </button>
          ))}
        </div>
      </Card>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <>
          {/* Trend Analysis */}
          <Card title="6-Month Performance Trend">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={monthlyData.trendAnalysis.last6Months}>
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="score"
                    stroke="#003580"
                    strokeWidth={3}
                    name="Overall Score"
                    dot={{ r: 5 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="budget"
                    stroke="#009543"
                    strokeWidth={3}
                    name="Budget Execution"
                    dot={{ r: 5 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="compliance"
                    stroke="#C4953B"
                    strokeWidth={3}
                    name="Compliance Rate"
                    dot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="text-sm font-bold text-blue-900 mb-2">Key Insights</h4>
              <div className="space-y-1">
                {monthlyData.trendAnalysis.insights.map((insight, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                    <SafeIcon icon={FiIcons.FiTrendingUp} className="text-blue-600 mt-0.5 shrink-0" />
                    <span>{insight}</span>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          {/* NDP6 Alignment */}
          <Card title="NDP6 Pillar Performance">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={monthlyData.ndp6Alignment.pillars}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="name" tick={{ fontSize: 10 }} />
                    <PolarRadiusAxis domain={[0, 100]} />
                    <Radar name="Progress" dataKey="progress" stroke="#003580" fill="#003580" fillOpacity={0.6} />
                    <Radar name="Target" dataKey="target" stroke="#C4953B" fill="#C4953B" fillOpacity={0.3} />
                    <Legend />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-4">
                {monthlyData.ndp6Alignment.pillars.map((pillar, idx) => (
                  <div key={idx} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-bold text-slate-800 text-sm">{pillar.name}</h4>
                      <span
                        className={clsx(
                          'px-2 py-1 rounded text-xs font-bold',
                          pillar.status === 'Exceeding'
                            ? 'bg-green-100 text-green-700'
                            : pillar.status === 'On Track'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-red-100 text-red-700'
                        )}
                      >
                        {pillar.status}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs mb-2">
                      <span className="text-slate-600">Progress: {pillar.progress}%</span>
                      <span className="text-slate-600">Target: {pillar.target}%</span>
                      <span
                        className={clsx(
                          'font-bold',
                          pillar.variance >= 0 ? 'text-green-600' : 'text-red-600'
                        )}
                      >
                        {pillar.variance >= 0 ? '+' : ''}{pillar.variance}%
                      </span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div
                        className={clsx(
                          'h-2 rounded-full',
                          pillar.status === 'Exceeding'
                            ? 'bg-green-500'
                            : pillar.status === 'On Track'
                            ? 'bg-blue-500'
                            : 'bg-red-500'
                        )}
                        style={{ width: `${pillar.progress}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          {/* Recommendations */}
          <Card title="Strategic Recommendations">
            <div className="space-y-4">
              {monthlyData.recommendations.map((rec, idx) => (
                <div
                  key={idx}
                  className={clsx(
                    'p-4 rounded-lg border',
                    rec.priority === 'critical'
                      ? 'bg-red-50 border-red-200'
                      : rec.priority === 'high'
                      ? 'bg-orange-50 border-orange-200'
                      : 'bg-amber-50 border-amber-200'
                  )}
                >
                  <div className="flex items-start gap-3">
                    <SafeIcon
                      icon={FiIcons.FiAlertCircle}
                      className={clsx(
                        'text-xl mt-0.5',
                        rec.priority === 'critical'
                          ? 'text-red-600'
                          : rec.priority === 'high'
                          ? 'text-orange-600'
                          : 'text-amber-600'
                      )}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span
                          className={clsx(
                            'px-2 py-1 rounded text-xs font-bold uppercase',
                            rec.priority === 'critical'
                              ? 'bg-red-600 text-white'
                              : rec.priority === 'high'
                              ? 'bg-orange-600 text-white'
                              : 'bg-amber-600 text-white'
                          )}
                        >
                          {rec.priority}
                        </span>
                        <span className="text-xs text-slate-500">Timeline: {rec.timeline}</span>
                      </div>
                      <h4 className="font-bold text-slate-900 mb-2">{rec.recommendation}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-slate-600 font-medium">Rationale:</span>
                          <p className="text-slate-700 mt-1">{rec.rationale}</p>
                        </div>
                        <div>
                          <span className="text-slate-600 font-medium">Required Action:</span>
                          <p className="text-slate-700 mt-1">{rec.action}</p>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-slate-200">
                        <div className="flex items-center gap-2 text-xs text-slate-600">
                          <SafeIcon icon={FiIcons.FiUser} />
                          <span>
                            <strong>Responsible:</strong> {rec.responsible}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </>
      )}

      {activeTab === 'ministries' && (
        <>
          {/* Ministry Ranking */}
          <Card title="Ministry Performance Ranking">
            <div className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyData.ministryPerformance} layout="vertical">
                  <XAxis type="number" domain={[0, 100]} />
                  <YAxis type="category" dataKey="id" width={80} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="score" radius={[0, 4, 4, 0]}>
                    {monthlyData.ministryPerformance.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.score >= 80 ? '#009543' : entry.score >= 60 ? '#C4953B' : '#D21034'}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Ministry Detail Cards */}
          <div className="space-y-4">
            {monthlyData.ministryPerformance.map((ministry, idx) => (
              <Card
                key={idx}
                className={clsx(
                  'hover:shadow-lg transition-all border-l-4',
                  ministry.rank === 1
                    ? 'border-l-green-500'
                    : ministry.rank === 2
                    ? 'border-l-blue-500'
                    : ministry.rank === 3
                    ? 'border-l-amber-500'
                    : 'border-l-slate-300'
                )}
              >
                <div className="space-y-4">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      <div
                        className={clsx(
                          'flex items-center justify-center w-12 h-12 rounded-full text-white font-black text-xl',
                          ministry.rank === 1
                            ? 'bg-grn-gold'
                            : ministry.rank === 2
                            ? 'bg-slate-400'
                            : ministry.rank === 3
                            ? 'bg-amber-500'
                            : 'bg-slate-300'
                        )}
                      >
                        {ministry.rank}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-bold text-slate-900">{ministry.name}</h3>
                          <span className="px-2 py-1 bg-grn-navy/10 text-grn-navy text-xs font-bold rounded">
                            {ministry.id}
                          </span>
                          <span
                            className={clsx(
                              'px-2 py-1 rounded text-xs font-medium flex items-center gap-1',
                              ministry.trend === 'up'
                                ? 'bg-green-100 text-green-700'
                                : ministry.trend === 'down'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-slate-100 text-slate-700'
                            )}
                          >
                            <SafeIcon
                              icon={
                                ministry.trend === 'up'
                                  ? FiIcons.FiTrendingUp
                                  : ministry.trend === 'down'
                                  ? FiIcons.FiTrendingDown
                                  : FiIcons.FiMinus
                              }
                            />
                            {ministry.previousScore} → {ministry.score}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="text-center ml-4">
                      <div className={clsx('text-4xl font-black mb-1', getScoreColor(ministry.score))}>
                        {ministry.score}%
                      </div>
                      <div className="text-xs text-slate-500 uppercase tracking-wider">Overall Score</div>
                    </div>
                  </div>

                  {/* KPI Grid */}
                  <div className="grid grid-cols-5 gap-3">
                    {Object.entries(ministry.kpis).map(([key, value]) => (
                      <div key={key} className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-center">
                        <div className={clsx('text-2xl font-black mb-1', getScoreColor(value))}>{value}%</div>
                        <div className="text-xs text-slate-600 capitalize">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Highlights & Concerns */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-slate-100">
                    <div>
                      <h4 className="text-xs font-bold text-green-700 uppercase tracking-wider mb-2">
                        Highlights
                      </h4>
                      <div className="space-y-1">
                        {ministry.highlights.map((highlight, hIdx) => (
                          <div key={hIdx} className="flex items-start gap-2 text-sm text-slate-700">
                            <SafeIcon icon={FiIcons.FiCheckCircle} className="text-green-600 mt-0.5 shrink-0" />
                            <span>{highlight}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-red-700 uppercase tracking-wider mb-2">Concerns</h4>
                      {ministry.concerns.length > 0 ? (
                        <div className="space-y-1">
                          {ministry.concerns.map((concern, cIdx) => (
                            <div key={cIdx} className="flex items-start gap-2 text-sm text-slate-700">
                              <SafeIcon icon={FiIcons.FiAlertCircle} className="text-red-600 mt-0.5 shrink-0" />
                              <span>{concern}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-500 italic">No concerns identified</p>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}

      {activeTab === 'projects' && (
        <>
          {/* Project Milestones Summary */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="text-center p-4 border-l-4 border-l-green-500">
              <div className="text-3xl font-black text-green-700">{monthlyData.projectMilestones.achieved}</div>
              <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Achieved</div>
            </Card>
            <Card className="text-center p-4 border-l-4 border-l-blue-500">
              <div className="text-3xl font-black text-blue-700">{monthlyData.projectMilestones.onTrack}</div>
              <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">On Track</div>
            </Card>
            <Card className="text-center p-4 border-l-4 border-l-amber-500">
              <div className="text-3xl font-black text-amber-700">{monthlyData.projectMilestones.atRisk}</div>
              <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">At Risk</div>
            </Card>
            <Card className="text-center p-4 border-l-4 border-l-red-600">
              <div className="text-3xl font-black text-red-700">{monthlyData.projectMilestones.delayed}</div>
              <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Delayed</div>
            </Card>
            <Card className="text-center p-4 border-l-4 border-l-slate-500">
              <div className="text-3xl font-black text-slate-700">{monthlyData.projectMilestones.missed}</div>
              <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Missed</div>
            </Card>
          </div>

          {/* Major Achievements */}
          <Card title="Major Milestones Achieved This Month">
            <div className="space-y-4">
              {monthlyData.projectMilestones.majorAchievements.map((achievement, idx) => (
                <div key={idx} className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiIcons.FiCheckCircle} className="text-green-600 text-2xl mt-1 shrink-0" />
                    <div className="flex-1">
                      <h4 className="font-bold text-slate-900 mb-1">{achievement.milestone}</h4>
                      <p className="text-sm text-slate-700 mb-2">
                        <strong>{achievement.project}</strong> - {achievement.ministry}
                      </p>
                      <p className="text-sm text-green-800 italic">Impact: {achievement.impact}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Critical Delays */}
          <Card title="Critical Delays Requiring Intervention">
            <div className="space-y-4">
              {monthlyData.projectMilestones.criticalDelays.map((delay, idx) => (
                <div key={idx} className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiIcons.FiAlertTriangle} className="text-red-600 text-2xl mt-1 shrink-0" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-bold text-slate-900">{delay.milestone}</h4>
                        <span className="px-2 py-1 bg-red-600 text-white text-xs font-bold rounded">
                          {delay.daysDelayed} DAYS DELAYED
                        </span>
                      </div>
                      <p className="text-sm text-slate-700 mb-2">
                        <strong>{delay.project}</strong> - {delay.ministry}
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-slate-600 font-medium">Reason:</span>
                          <p className="text-slate-800 mt-1">{delay.reason}</p>
                        </div>
                        <div>
                          <span className="text-slate-600 font-medium">Action Taken:</span>
                          <p className="text-slate-800 mt-1">{delay.action}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </>
      )}

      {activeTab === 'compliance' && (
        <>
          {/* Compliance Overview */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="text-center p-4 border-l-4 border-l-grn-navy">
              <div className={clsx('text-3xl font-black', getScoreColor(monthlyData.complianceMetrics.overallRate))}>
                {monthlyData.complianceMetrics.overallRate}%
              </div>
              <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Overall Rate</div>
            </Card>
            <Card className="text-center p-4 border-l-4 border-l-green-500">
              <div className="text-3xl font-black text-green-700">{monthlyData.complianceMetrics.onTrack}</div>
              <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">On Track</div>
            </Card>
            <Card className="text-center p-4 border-l-4 border-l-amber-500">
              <div className="text-3xl font-black text-amber-700">{monthlyData.complianceMetrics.warnings}</div>
              <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Warnings</div>
            </Card>
            <Card className="text-center p-4 border-l-4 border-l-red-600">
              <div className="text-3xl font-black text-red-700">{monthlyData.complianceMetrics.breaches}</div>
              <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Breaches</div>
            </Card>
          </div>

          {/* Compliance by Category */}
          <Card title="Compliance Performance by Category">
            <div className="space-y-4">
              {monthlyData.complianceMetrics.categories.map((category, idx) => (
                <div key={idx} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-bold text-slate-900">{category.category}</h4>
                        <span
                          className={clsx(
                            'px-2 py-1 rounded text-xs font-bold',
                            category.status === 'Critical'
                              ? 'bg-red-100 text-red-700'
                              : category.status === 'At Risk'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-green-100 text-green-700'
                          )}
                        >
                          {category.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-600">
                        <span>Rate: {category.rate}%</span>
                        <span>Target: {category.target}%</span>
                        <span>Breaches: {category.breaches}</span>
                      </div>
                    </div>
                    <div className={clsx('text-4xl font-black', getScoreColor(category.rate))}>
                      {category.rate}%
                    </div>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-3">
                    <div
                      className={clsx('h-3 rounded-full', getScoreBgColor(category.rate))}
                      style={{ width: `${category.rate}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Top & Bottom Performers */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card title="Top Compliant Ministries">
              <div className="space-y-2">
                {monthlyData.complianceMetrics.topCompliantMinistries.map((ministry, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center justify-center w-8 h-8 bg-grn-gold text-white rounded-full font-bold">
                      {idx + 1}
                    </div>
                    <span className="text-sm font-medium text-slate-800">{ministry}</span>
                    <SafeIcon icon={FiIcons.FiCheckCircle} className="text-green-600 ml-auto" />
                  </div>
                ))}
              </div>
            </Card>
            <Card title="Needs Improvement">
              <div className="space-y-2">
                {monthlyData.complianceMetrics.bottomCompliantMinistries.map((ministry, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center justify-center w-8 h-8 bg-red-600 text-white rounded-full font-bold">
                      {idx + 1}
                    </div>
                    <span className="text-sm font-medium text-slate-800">{ministry}</span>
                    <SafeIcon icon={FiIcons.FiAlertTriangle} className="text-red-600 ml-auto" />
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </>
      )}

      {activeTab === 'budget' && (
        <>
          {/* Budget Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="text-center p-4 border-l-4 border-l-grn-navy">
              <div className={clsx('text-3xl font-black', getScoreColor(monthlyData.budgetExecution.nationalRate))}>
                {monthlyData.budgetExecution.nationalRate}%
              </div>
              <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">National Rate</div>
            </Card>
            <Card className="text-center p-4 border-l-4 border-l-blue-500">
              <div className="text-2xl font-black text-blue-700">
                {formatCurrency(monthlyData.budgetExecution.totalAllocated)}
              </div>
              <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Allocated</div>
            </Card>
            <Card className="text-center p-4 border-l-4 border-l-green-500">
              <div className="text-2xl font-black text-green-700">
                {formatCurrency(monthlyData.budgetExecution.totalDisbursed)}
              </div>
              <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Disbursed</div>
            </Card>
            <Card className="text-center p-4 border-l-4 border-l-red-600">
              <div className="text-2xl font-black text-red-700">
                {formatCurrency(Math.abs(monthlyData.budgetExecution.variance))}
              </div>
              <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Variance</div>
            </Card>
          </div>

          {/* Budget Execution by Ministry */}
          <Card title="Budget Execution by Ministry">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyData.budgetExecution.byMinistry}>
                  <XAxis dataKey="ministry" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Bar dataKey="rate" radius={[4, 4, 0, 0]}>
                    {monthlyData.budgetExecution.byMinistry.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.rate >= 80 ? '#009543' : entry.rate >= 60 ? '#C4953B' : '#D21034'}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Underspending Issues */}
          <Card title="Critical Underspending Issues">
            <div className="space-y-4">
              {monthlyData.budgetExecution.underspending.map((issue, idx) => (
                <div key={idx} className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiIcons.FiAlertCircle} className="text-red-600 text-2xl mt-1 shrink-0" />
                    <div className="flex-1">
                      <h4 className="font-bold text-slate-900 mb-2">{issue.ministry}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3 text-sm">
                        <div>
                          <span className="text-slate-600 font-medium">Allocated:</span>
                          <p className="text-slate-900 font-bold">{formatCurrency(issue.allocated)}</p>
                        </div>
                        <div>
                          <span className="text-slate-600 font-medium">Disbursed:</span>
                          <p className="text-slate-900 font-bold">{formatCurrency(issue.disbursed)}</p>
                        </div>
                        <div>
                          <span className="text-slate-600 font-medium">Gap:</span>
                          <p className="text-red-700 font-bold">{formatCurrency(issue.gap)}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-slate-600 font-medium">Reason:</span>
                          <p className="text-slate-800 mt-1">{issue.reason}</p>
                        </div>
                        <div>
                          <span className="text-slate-600 font-medium">Required Action:</span>
                          <p className="text-slate-800 mt-1">{issue.action}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </>
      )}

      {/* Legal Framework Notice */}
      <Card className="bg-grn-navy text-white">
        <div className="flex gap-3">
          <SafeIcon icon={FiIcons.FiInfo} className="text-xl shrink-0 mt-0.5" />
          <div className="text-xs">
            <p className="font-bold mb-2">Monthly Performance Review Framework</p>
            <p className="opacity-90">
              This monthly performance dashboard consolidates data from all GovTrack modules to provide comprehensive
              oversight for Cabinet briefings and strategic decision-making. All metrics are tracked in real-time and
              verified against statutory requirements under the <strong>Public Procurement Act (2015)</strong>,{' '}
              <strong>State Finance Act (1991)</strong>, <strong>NDP6 IMEP Framework</strong>, and{' '}
              <strong>Performance Management Framework</strong>. Reports are generated automatically on the 5th of each
              month for the previous month's performance.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};